import React, { useMemo, useState } from 'react';
import Table from '../../../../theme/ui/Atoms/Table/Table';
import Button from '../../../../theme/ui/Atoms/Button';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Stack,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import { AirConnectIcon, ReinstallIcon, TrashIcon } from '../../../../assets';
import { useTranslation } from 'react-i18next';
import { getIn, useFormik } from 'formik';
import * as yup from 'yup';
import useBack from '../../../../shared/util/route-dom/hooks/useBack';
import { useNavigate, useParams } from 'react-router-dom';
import Input from '../../../../theme/ui/Atoms/Input';
import InputSelect from '../../../../theme/ui/Atoms/InputSelect';
import BackButton from '../BackButton';
import CommanderAirConnectAPI from '../../../../service/device/CommanderAirConnectAPI/CommanderAirConnectAPI';
import {
  AirConnectNode,
  CommanderAirConnectCreateComposableDeviceItemAPI,
  ComposableDeviceControlUnitMap,
} from '../../../../service/device/CommanderAirConnectAPI/CommanderAirConnectAPI.model';
import useParseErrorMessage from '../../../../shared/hooks/useParseErrorMessage';
import useErrorMessage from '../../../../shared/hooks/useErrorMessage';
import FlexeserveDeviceAPI, {
  M_SECONDS_POOLING_SETUP,
} from '../../../../service/device/FlexeserveDevice/FlexeserveDeviceAPI';
import { DevicesStoreInfoResponse } from '../../../../service/store/StoreService.model';
import ZoneManagerConnected from '../ZoneManagerConnected';
import useMyWorkspace from '../../../../redux/workspace/hooks/useMyWorkspace';
import useMachineTypes from '../../../../redux/workspace/hooks/useMachineTypes';
import cleanObject from '../../../../shared/util/lodash/object/cleanObject';
import { isNotNullOrUndefined } from '../../../../shared/util/collection/isNotNullOrUndefined';
import {
  AIR_CONNECT_NAME_OPTIONS,
  AIR_CONNECT_ZONE_DESCRIPTION_OPTIONS,
} from '../../../../service/device/CommanderAirConnectAPI/CommanderAirConnectAPI.constants';
import useDevicesWithComposableDevice, {
  ComposableDevicesStoreInfoResponse,
} from '../../../../service/store/hooks/useDevicesWithComposableDevice';
import { INSTALLATION_TYPE, isUpdating } from '../utils';
import GroupAPI, {
  useGetGroupByIdQuery,
} from '../../../../service/group/GroupAPI';
import {
  ComposableDevice,
  GroupWithComposableDevice,
} from '../../../../service/group/group.model';
import useMessage from '../../../../shared/hooks/useMessage';
import { ModalInfo } from '../../../../theme/ui/Atoms/Modal';
import _ from 'lodash';
import { IconButton } from '../../../../theme/ui/Atoms/Button/Button.stories';
import { fromBase64 } from '../../../../shared/util/base64';
import { MachineType } from '../../../../redux/workspace/WorkspaceAPI.model';

// one array of at least one string
const validationSchema = yup
  .array()
  .of(
    yup.object({
      mac: yup.string().required(),
      name: yup
        .string()
        .required('initial_setup.device_name_required')
        .nullable(),
      composableDevice: yup.object({
        machineTypeId: yup.string().required(),
      }),
      serialnumber: yup.string().nullable(),
    })
  )
  .test(
    'unique',
    'initial_setup.scan_air_connect.serial_number_unique',
    (value, c) => {
      const serialNumbers = value?.filter((v) => v.serialnumber);

      const isUnique =
        new Set(serialNumbers?.map((v) => v.serialnumber)).size ===
        serialNumbers?.length;

      return isUnique
        ? true
        : c.createError({
            path: 'serialnumberError',
            message: 'initial_setup.scan_air_connect.serial_number_unique',
          });
    }
  )
  .test(
    'uniqueMac',
    'initial_setup.scan_air_connect.mac_address_unique',
    (value, c) => {
      const macAddresses = value?.filter((v) => v.mac);

      const isUnique =
        new Set(macAddresses?.map((v) => v.mac)).size === macAddresses?.length;

      return isUnique
        ? true
        : c.createError({
            path: 'macAddressError',
            message: 'initial_setup.scan_air_connect.mac_address_unique',
          });
    }
  );

const getDefaultMachineType = (machineTypes?: any[], nodeType?: string) => {
  return machineTypes?.find(
    (machineType) => machineType.labels.nodeType === nodeType
  );
};

const isOldNode = (
  nodes: AirConnectNode[] = [],
  item:
    | ComposableDevicesStoreInfoResponse
    | CommanderAirConnectCreateComposableDeviceItemAPI
    | null
) => {
  return nodes.find((node) => node.macAddress === item?.mac) === undefined;
};

const duplicatedValues = (
  flexeServeList: ComposableDevicesStoreInfoResponse[],
  groups: GroupWithComposableDevice[] = [],
  nodes: AirConnectNode[] = []
) => {
  const noNodeLabel = {
    type: 'error',
    label: 'initial_setup.connect_commander.no_node_mac',
  };
  const duplicatedNode = {
    type: 'duplicated',
    label: 'initial_setup.connect_commander.node_duplicated',
  };
  const itemsDuplicated: {
    id: string;
    type: string;
    label: string;
  }[] = [];

  flexeServeList.forEach((item, index) => {
    if (isOldNode(nodes, item)) {
      itemsDuplicated.push({
        id: item.uuid,
        type: noNodeLabel.type,
        label: noNodeLabel.label,
      });
      return;
    }
    const duplicated = flexeServeList.find(
      (otherItem, otherIndex) =>
        otherIndex !== index && item.mac === otherItem.mac
    );
    if (duplicated) {
      const sameZoneMapping = _.isEqual(
        Object.values(item.composableZoneMapping ?? {}),
        Object.values(duplicated.composableZoneMapping ?? {})
      );

      const groupItem1 = groups.find(
        (itemGroup) => fromBase64(itemGroup.id) === item.composableDeviceId
      );
      const groupItem2 = groups.find(
        (itemGroup) =>
          fromBase64(itemGroup.id) === duplicated.composableDeviceId
      );
      const haveSameControlUnits = _.isEqual(
        Object.values(groupItem1?.composableDevice.controlUnits ?? {}),
        Object.values(groupItem2?.composableDevice.controlUnits ?? {})
      );

      if (sameZoneMapping && haveSameControlUnits) {
        if (
          !itemsDuplicated.find(
            (duplicatedItem) => item.uuid === duplicatedItem.id
          )
        ) {
          itemsDuplicated.push({
            id: item.uuid,
            type: duplicatedNode.type,
            label: duplicatedNode.label,
          });
          if (
            !itemsDuplicated.find(
              (duplicatedItem) => duplicatedItem.id === duplicated.unitId
            )
          ) {
            itemsDuplicated.push({
              id: duplicated.uuid,
              type: duplicatedNode.type,
              label: duplicatedNode.label,
            });
          }
        }
      }

      if (
        !sameZoneMapping &&
        !Object.values(item?.composableZoneMapping ?? {}).length
      ) {
        if (
          !itemsDuplicated.find(
            (duplicatedItem) => item.uuid === duplicatedItem.id
          )
        ) {
          itemsDuplicated.push({
            id: item.uuid,
            type: noNodeLabel.type,
            label: noNodeLabel.label,
          });
        }
      }
      const groupItem = groups.find(
        (itemGroup) => fromBase64(itemGroup.id) === item.composableDeviceId
      );
      if (
        Object.keys(item.composableZoneMapping ?? {}).length === 0 &&
        groupItem?.composableDevice.controlUnits.length === 0 &&
        !itemsDuplicated.find(
          (duplicatedItem) => item.uuid === duplicatedItem.id
        )
      ) {
        itemsDuplicated.push({
          id: item.uuid,
          type: noNodeLabel.type,
          label: noNodeLabel.label,
        });
      }
    }
  });

  return itemsDuplicated;
};
const parseNodesToInitValues = (
  nodes?: AirConnectNode[],
  flexeserveDevice?: DevicesStoreInfoResponse,
  machineTypes?: MachineType[],
  flexeServeList: ComposableDevicesStoreInfoResponse[] = []
): CommanderAirConnectCreateComposableDeviceItemAPI[] | undefined => {
  if (!nodes || !flexeserveDevice) return undefined;

  return [
    ...flexeServeList.map((flexeServeDevice) => {
      const node = nodes.find(
        (node) => node.macAddress === flexeServeDevice.mac
      );

      const defaultMachineType = getDefaultMachineType(
        machineTypes,
        node?.nodeType.toString()
      );
      const addressRelated = Array.from(
        { length: node?.numberOfControllers || 0 },
        (_, i) => ((node?.nodeAddress ?? 0) + i).toString()
      );
      const controlUnitsRelated =
        flexeserveDevice?.shelfMappings
          ?.filter(isNotNullOrUndefined)
          .filter((shelfMapping) =>
            addressRelated.includes(shelfMapping.cuaddr)
          ) || [];
      const controlUnits: ComposableDeviceControlUnitMap[] =
        flexeServeDevice.composableDevice?.controlUnits ||
        controlUnitsRelated.map((controlUnit) => ({
          deviceId: flexeserveDevice?.commanderId,
          controlUnitId: controlUnit.cuid.toString(),
        }));
      const zoneMapping: Record<string, string> =
        flexeServeDevice.composableZoneMapping ||
        controlUnitsRelated.reduce((acc, controlUnit, currentIndex) => {
          return {
            ...acc,
            [(currentIndex + 1).toString()]: controlUnit.cuaddr,
          };
        }, {});

      const labels = flexeServeDevice?.composableDevice?.labels || {};
      const extraLabels =
        flexeServeDevice?.composableDevice?.controlUnits.reduce(
          (acc, _index, currentIndex) => {
            return {
              ...acc,
              [`zone_${currentIndex}_description`]:
                labels[`zone_${currentIndex}_description`] ?? '',
            };
          },
          {}
        );
      return {
        mac: flexeServeDevice.mac || node?.macAddress || '',
        name: flexeServeDevice?.unitName ?? null,
        serialnumber: flexeServeDevice?.serialnumber ?? null,
        model: 'AIR_CONNECT',
        composableDevice: {
          machineTypeId:
            flexeServeDevice?.composableDevice?.machineTypeId ??
            defaultMachineType?.id,
          controlUnits,
          labels: {
            'node.bitmask':
              labels['node.bitmask'] ?? (node?.bitmask.toString() || ''),
            'node.nodeNumber':
              labels['node.nodeNumber'] ?? (node?.nodeNumber.toString() || ''),
            'node.nodeAddress':
              labels['node.nodeAddress'] ??
              (node?.nodeAddress.toString() || ''),
            'node.numberOfControllersInstalled':
              labels['node.numberOfControllersInstalled'] ??
              (node?.numberOfControllersInstalled.toString() || ''),
            'node.numberOfControllers':
              labels['node.numberOfControllers'] ??
              (node?.numberOfControllers.toString() || ''),
            'node.nodeType':
              labels['node.nodeType'] ?? (node?.nodeType.toString() || ''),
            'node.nodeReachable':
              labels['node.nodeReachable'] ??
              (node?.nodeReachable.toString() || ''),
            ...extraLabels,
          },
        },
        zoneMapping,
      };
    }),
    ...nodes
      .filter(
        (node) =>
          !flexeServeList.find(
            (flexeserveNode) => flexeserveNode.mac === node.macAddress
          )
      )
      .map((node) => {
        const defaultMachineType = getDefaultMachineType(
          machineTypes,
          node.nodeType.toString()
        );
        const addressRelated = Array.from(
          { length: node.numberOfControllers },
          (_, i) => (node.nodeAddress + i).toString()
        );
        const controlUnitsRelated =
          flexeserveDevice?.shelfMappings
            ?.filter(isNotNullOrUndefined)
            .filter((shelfMapping) =>
              addressRelated.includes(shelfMapping.cuaddr)
            ) || [];
        const controlUnits: ComposableDeviceControlUnitMap[] =
          controlUnitsRelated.map((controlUnit) => ({
            deviceId: flexeserveDevice?.commanderId,
            controlUnitId: controlUnit.cuid.toString(),
          }));
        const zoneMapping: Record<string, string> = controlUnitsRelated.reduce(
          (acc, controlUnit, currentIndex) => {
            return {
              ...acc,
              [(currentIndex + 1).toString()]: controlUnit.cuaddr,
            };
          },
          {}
        );
        return {
          mac: node.macAddress,
          name: null,
          serialnumber: null,
          model: 'AIR_CONNECT',
          composableDevice: {
            machineTypeId: defaultMachineType?.id,
            controlUnits,
            labels: {
              'node.bitmask': node.bitmask.toString(),
              'node.nodeNumber': node.nodeNumber.toString(),
              'node.nodeAddress': node.nodeAddress.toString(),
              'node.numberOfControllersInstalled':
                node.numberOfControllersInstalled.toString(),
              'node.numberOfControllers': node.numberOfControllers.toString(),
              'node.nodeType': node.nodeType.toString(),
              'node.nodeReachable': node.nodeReachable.toString(),
            },
          },
          zoneMapping,
        };
      }),
  ];
};
const isNewNode = (
  value: CommanderAirConnectCreateComposableDeviceItemAPI | null,
  devices: ComposableDevicesStoreInfoResponse[] = []
) => {
  return devices.find((device) => device.mac === value?.mac) === undefined;
};

const getSortedList = (
  flexeServeList: ComposableDevicesStoreInfoResponse[]
) => {
  return [...flexeServeList]
    .filter((item) => item.visible)
    .sort((a, b) => (a?.mac ?? '').localeCompare(b?.mac ?? ''));
};

const getDevicesToUpdate = (
  values: CommanderAirConnectCreateComposableDeviceItemAPI[],
  flexeserveDevicesList: ComposableDevicesStoreInfoResponse[]
) => {
  return values
    .map((device) => {
      const oldFlexeserveDevice = flexeserveDevicesList.find(
        (flexeserveDevice) => device.mac === flexeserveDevice.mac
      );
      if (oldFlexeserveDevice) {
        const flexeserveDevice = {
          ...oldFlexeserveDevice,
          ...device,
          unitName: device.name,
          composableZoneMapping: device.zoneMapping,
          composableDevice: {
            ...device.composableDevice,
          } as ComposableDevice,
        } as Partial<ComposableDevicesStoreInfoResponse>;
        const newValue = values.find((value) => value.mac === device.mac);
        const composableDevice = {
          id: flexeserveDevice.composableDeviceId ?? '',
          data: {
            name: flexeserveDevice.unitName,
            composableDevice:
              newValue?.composableDevice ?? ({} as ComposableDevice),
          } as Partial<GroupWithComposableDevice>,
        };

        return {
          flexeserveDevice,
          composableDevice,
        };
      }

      return null;
    })
    .filter(isNotNullOrUndefined);
};

function DevicesSetUp() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const parseError = useParseErrorMessage();
  const showErrorMessage = useErrorMessage();
  const showMessage = useMessage();
  const theme = useTheme();
  const {
    commanderId = '',
    serialNumber = '',
    storeId = '',
    installationType = INSTALLATION_TYPE.store,
  } = useParams<{
    commanderId: string;
    serialNumber: string;
    storeId: string;
    installationType: INSTALLATION_TYPE;
  }>();

  const [deviceToDelete, setDeviceToDelete] =
    useState<CommanderAirConnectCreateComposableDeviceItemAPI | null>(null);
  const [displayConfirmModal, setDisplayConfirmModal] =
    useState<boolean>(false);

  const updating = isUpdating(installationType);

  const {
    isFetching: isFetchingNodesInstalled,
    data: nodesInstalled,
    error: errorNodesInstalled,
  } = CommanderAirConnectAPI.useGetNodesInstalledQuery(commanderId, {
    refetchOnMountOrArgChange: true,
    skip: !commanderId,
  });

  const [
    registerComposableDevice,
    {
      isLoading: isRegisteringComposableDevice,
      error: errorRegisterComposableDevice,
    },
  ] = CommanderAirConnectAPI.useRegisterNewComposableDeviceMutation();

  const {
    data: myWorkspace,
    error: errorMyWorkspace,
    isFetching: isFetchingMyWorkspace,
  } = useMyWorkspace();

  const {
    data: flexeserveDevice,
    error: errorFlexeserveDevice,
    isFetching: isFetchingFlexeserveDevice,
  } = FlexeserveDeviceAPI.useGetDeviceByFlexeserveDeviceIdQuery(commanderId, {
    refetchOnMountOrArgChange: true,
    skip: !commanderId,
    pollingInterval: M_SECONDS_POOLING_SETUP,
  });

  const {
    data: flexeserveDevicesList = [],
    isFetching: isFetchingFlexeserveDevicesList,
    error: errorFetchingFlexeserveDevicesList,
  } = FlexeserveDeviceAPI.useGetStoreDevicesQuery(storeId, {
    skip: !updating,
  });

  const sortedFlexeserveDevices = useMemo(
    () => getSortedList(flexeserveDevicesList),
    [flexeserveDevicesList]
  );

  const { devices } = useDevicesWithComposableDevice(
    storeId,
    sortedFlexeserveDevices || []
  );

  const { data } = useGetGroupByIdQuery(storeId ?? '');
  const composableDevices = data?.childGroups;

  const [updateFlexeserveDevice] =
    FlexeserveDeviceAPI.useUpdateSetupDeviceMutation();

  const [
    deleteFlexeserveDevice,
    {
      isLoading: isDeletingFlexeserveDevice,
      error: errorDeletingFlexeserveDevice,
    },
  ] = FlexeserveDeviceAPI.useDeleteFlexeserveDeviceMutation();

  const [updateComposableDevice, { error: errorUpdatingGroup }] =
    GroupAPI.useUpdateGroupMutation();

  const {
    data: machineTypes,
    isLoading: isLoadingMachineTypes,
    error: errorMachineTypes,
  } = useMachineTypes(undefined);

  const [
    reinstallNode,
    { isLoading: isReinstallingNode, error: errorReinstallingNode },
  ] = CommanderAirConnectAPI.useReinstallNodeMutation();

  const [removeNode, { isLoading: isRemovingNode, error: errorRemovingNode }] =
    CommanderAirConnectAPI.useRemoveNodeMutation();

  const currentInitialValues = useMemo(
    () =>
      parseNodesToInitValues(
        nodesInstalled,
        flexeserveDevice,
        machineTypes,
        devices
      ) ?? [],
    [nodesInstalled, flexeserveDevice, machineTypes, devices]
  );

  const createOrUpdateDevice = (
    values: CommanderAirConnectCreateComposableDeviceItemAPI[]
  ) => {
    if (!updating) {
      return registerComposableDevice({
        workspaceId: myWorkspace?.id ?? '',
        storeId,
        data: {
          commanderSerialNum: serialNumber,
          devices: cleanObject(values),
          storeId,
        },
      }).unwrap();
    } else {
      // register just the new composable device
      const newDevices = values.filter(
        (device) => isNewNode(device, devices) && device.mac
      );

      return registerComposableDevice({
        workspaceId: myWorkspace?.id ?? '',
        storeId,
        data: {
          commanderSerialNum: serialNumber,
          devices: cleanObject(newDevices),
          storeId,
        },
      })
        .unwrap()
        .then(() => {
          const devicesToUpdate = getDevicesToUpdate(
            values,
            flexeserveDevicesList
          ).map(({ flexeserveDevice, composableDevice }) => ({
            updateFlexeserveDevice: updateFlexeserveDevice(flexeserveDevice),
            updateComposableDevice: updateComposableDevice(composableDevice),
          }));
          return Promise.allSettled(Object.values(devicesToUpdate));
        });
    }
  };

  const removeNodeInstance = (): Promise<null> => {
    const nodeToRemove = nodesInstalled?.find(
      (node) => node.macAddress === deviceToDelete?.mac
    );
    if (!nodeToRemove) {
      showErrorMessage(t('error.device_not_found'));
      return Promise.reject(t('error.device_not_found'));
    }
    return removeNode({
      commanderId,
      node: nodeToRemove,
    }).unwrap();
  };

  const onDelete = ({
    shouldRemoveFlexeserveDevice,
    shouldRemoveNode,
  }: {
    shouldRemoveFlexeserveDevice: boolean;
    shouldRemoveNode: boolean;
  }) => {
    let promise: Promise<null> | undefined;
    if (shouldRemoveFlexeserveDevice) {
      const flexeserveDevice = flexeserveDevicesList.find(
        (device) => device.mac === deviceToDelete?.mac
      );
      if (!flexeserveDevice) {
        showErrorMessage(t('error.device_not_found'));
        return;
      } else {
        promise = deleteFlexeserveDevice(flexeserveDevice.uuid ?? '')
          .unwrap()
          .then(() => {
            if (!shouldRemoveNode) return null;
            else {
              return removeNodeInstance();
            }
          });
      }
    }
    if (shouldRemoveNode && !shouldRemoveFlexeserveDevice) {
      promise = removeNodeInstance();
    }

    promise
      ?.then(() => {
        showMessage(
          t('initial_setup.connect_commander.device_deleted_successfully'),
          { variant: 'success' }
        );
      })
      .catch((error) => {
        showErrorMessage(
          t('initial_setup.connect_commander.device_delete_error', {
            error: parseError(error),
          })
        );
      })
      .finally(() => {
        setDisplayConfirmModal(false);
        setDeviceToDelete(null);
      });
  };
  const formik = useFormik({
    initialValues: currentInitialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      return createOrUpdateDevice(values)
        .then(() => navigate('../devices-overview-manager'))
        .catch(showErrorMessage);
    },
  });
  const createOrUpdateDeviceBack = useBack('/');
  const duplicated = useMemo(
    () =>
      duplicatedValues(
        sortedFlexeserveDevices,
        composableDevices,
        nodesInstalled
      ),
    [sortedFlexeserveDevices, composableDevices, nodesInstalled]
  );

  const loading =
    isFetchingNodesInstalled ||
    isRegisteringComposableDevice ||
    isFetchingFlexeserveDevice ||
    isFetchingMyWorkspace ||
    isLoadingMachineTypes ||
    isReinstallingNode ||
    isFetchingFlexeserveDevicesList ||
    isRemovingNode ||
    isDeletingFlexeserveDevice ||
    devices.length === 0;

  const error =
    errorNodesInstalled ||
    errorRegisterComposableDevice ||
    errorFlexeserveDevice ||
    errorMyWorkspace ||
    errorMachineTypes ||
    errorReinstallingNode ||
    errorFetchingFlexeserveDevicesList ||
    errorRemovingNode ||
    errorDeletingFlexeserveDevice ||
    errorUpdatingGroup;
  const colors = {
    error: {
      borderColor: theme.palette.error.dark,
      bgColor: theme.palette.error.main,
    },
    duplicated: {
      borderColor: '#FCB42D',
      bgColor: '#614F2D',
    },
    new: {
      borderColor: '#59AE30',
      bgColor: '#394E2E',
    },
  };

  return (
    <>
      <Container maxWidth={false} sx={{ width: '85vw', margin: 'auto' }}>
        <Box
          sx={(theme) => ({
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fill: theme.palette.text.primary,
          })}
        >
          <Stack direction={'row'} alignItems={'center'}>
            {loading ? <CircularProgress /> : <AirConnectIcon height={'2em'} />}
            <Typography
              variant="subtitle2"
              marginY={2}
              sx={{
                display: 'flex',
                marginLeft: 3,
                alignItems: 'center',
              }}
            >
              {t('initial_setup.scan_air_connect.node_found', {
                count: nodesInstalled?.length || 0,
              })}
              : {nodesInstalled?.length || 0}
            </Typography>
          </Stack>
        </Box>

        <Container
          maxWidth={false}
          sx={{ mt: 4, overflowX: 'auto', overflowY: 'hidden', width: '100%' }}
        >
          {!!error && (
            <Alert severity="error" sx={{ my: 2 }}>
              {parseError(error)}
            </Alert>
          )}
          <Table
            sx={{
              '& th.header': {
                minWidth: 300,
                textAlign: 'left',
              },
            }}
          >
            <caption></caption>

            <thead style={{ border: 'none' }}>
              <tr>
                <Typography
                  component={'th'}
                  className="header"
                  color="primary"
                  variant="subtitle2"
                >
                  {t('initial_setup.scan_air_connect.mac_address')}
                </Typography>
                <Typography
                  component={'th'}
                  className="header"
                  color="primary"
                  variant="subtitle2"
                >
                  {t('initial_setup.scan_air_connect.device_name')}
                </Typography>

                <Typography
                  component={'th'}
                  className="header"
                  color="primary"
                  variant="subtitle2"
                >
                  {t('initial_setup.scan_air_connect.device_type')}
                </Typography>
                <Typography
                  component={'th'}
                  className="header"
                  color="primary"
                  variant="subtitle2"
                >
                  {t('initial_setup.scan_air_connect.device_description')}
                </Typography>
                <Typography
                  component={'th'}
                  color="primary"
                  variant="subtitle2"
                >
                  {t('initial_setup.scan_air_connect.device_action_test')}
                </Typography>
                <Typography
                  component={'th'}
                  color="primary"
                  variant="subtitle2"
                  sx={{
                    width: '130px',
                  }}
                >
                  {t('initial_setup.connect_commander.reinstall')}
                </Typography>
                {updating && (
                  <Typography
                    component={'th'}
                    color="primary"
                    variant="subtitle2"
                    sx={{
                      width: '130px',
                    }}
                  >
                    {t('initial_setup.connect_commander.delete')}
                  </Typography>
                )}
              </tr>
            </thead>
            <tbody>
              {formik.values?.map((device, i) => {
                const flexeServeDevice = sortedFlexeserveDevices[i];
                const itemDuplicated = duplicated.find(
                  (duplicatedItem) =>
                    duplicatedItem.id === flexeServeDevice?.uuid
                );
                const item =
                  colors[itemDuplicated?.type as keyof typeof colors];
                const border = item?.borderColor || '';

                const bgColor = item?.bgColor || '';

                return (
                  <Paper
                    component={'tr'}
                    key={device.mac + i}
                    elevation={undefined}
                  >
                    <td style={{ position: 'relative' }}>
                      {itemDuplicated && (
                        <Badge borderColor={border} bgcolor={bgColor}>
                          <Typography>{t(itemDuplicated.label)}</Typography>
                        </Badge>
                      )}
                      {device.mac && isNewNode(device, devices) && (
                        <Badge borderColor="#59AE30" bgcolor="#394E2E">
                          <Typography>
                            {t('initial_setup.connect_commander.new_node')}
                          </Typography>
                        </Badge>
                      )}
                      <Typography variant="body1">{device.mac}</Typography>
                    </td>
                    <td colSpan={2} style={{ padding: '1em 0px' }}>
                      <Grid container spacing={2}>
                        <Grid size={6}>
                          <InputSelect
                            select
                            id={`[${i}].name`}
                            name={`[${i}].name`}
                            options={AIR_CONNECT_NAME_OPTIONS?.map((name) => ({
                              value: name,
                              text: name,
                            }))}
                            placeholder={t(
                              'initial_setup.scan_air_connect.device_name'
                            )}
                            fullWidth
                            required
                            aria-required
                            value={formik.values[i].name}
                            onChange={(v: string) =>
                              formik.setFieldValue(`${i}.name`, v)
                            }
                            onBlur={formik.handleBlur}
                            error={
                              formik.errors[i]?.name &&
                              Boolean(formik.errors[i]?.name)
                            }
                            helperText={t(formik.errors[i]?.name ?? '')}
                          />
                        </Grid>
                        <Grid size={6}>
                          <InputSelect
                            select
                            id={`[${i}].device_type`}
                            name={`[${i}].device_type`}
                            options={machineTypes?.map((machineType) => ({
                              value: machineType.id,
                              text: machineType.name,
                            }))}
                            disable={isLoadingMachineTypes}
                            placeholder={t(
                              'initial_setup.scan_air_connect.device_type'
                            )}
                            fullWidth
                            required
                            aria-required
                            value={
                              formik.values[i].composableDevice.machineTypeId
                            }
                            onChange={(v: string) =>
                              formik.setFieldValue(
                                `${i}.composableDevice.machineTypeId`,
                                v
                              )
                            }
                            onBlur={formik.handleBlur}
                          />
                        </Grid>
                        <Grid textAlign={'left'} size={12}>
                          <Input
                            label={t(
                              'initial_setup.scan_air_connect.serial_number'
                            )}
                            placeholder={t(
                              'initial_setup.scan_air_connect.serial_number'
                            )}
                            fullWidth
                            id={`[${i}].serialnumber`}
                            name={`[${i}].serialnumber`}
                            value={formik.values[i].serialnumber}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </Grid>
                      </Grid>
                    </td>
                    <td>
                      <Stack
                        textAlign={'left'}
                        py={1}
                        spacing={2}
                        height={'100%'}
                        justifyContent={
                          device.composableDevice.controlUnits.length > 1
                            ? 'space-around'
                            : 'flex-start'
                        }
                      >
                        {device.composableDevice.controlUnits.map(
                          (controlUnit, j) => (
                            <InputSelect
                              sx={[
                                device.composableDevice.controlUnits.length > 1
                                  ? {
                                      marginTop: 0,
                                    }
                                  : {
                                      marginTop: '4px',
                                    },
                              ]}
                              key={controlUnit.controlUnitId}
                              select
                              id={`[${i}].composableDevice.labels.zone_${j}_description`}
                              name={`[${i}].composableDevice.labels.zone_${j}_description`}
                              options={AIR_CONNECT_ZONE_DESCRIPTION_OPTIONS?.map(
                                (description) => ({
                                  value: description,
                                  text: description,
                                })
                              )}
                              placeholder={t(
                                'initial_setup.scan_air_connect.placeholder_zone_description'
                              )}
                              value={getIn(
                                formik.values,
                                `[${i}].composableDevice.labels.zone_${j}_description`
                              )}
                              //   onChange={formik.handleChange}
                              onChange={(v: string) =>
                                formik.setFieldValue(
                                  `${i}.composableDevice.labels.zone_${j}_description`,
                                  v
                                )
                              }
                              onBlur={formik.handleBlur}
                            />
                          )
                        )}
                      </Stack>
                    </td>
                    <td>
                      <Stack
                        textAlign={'left'}
                        height={'100%'}
                        py={1}
                        spacing={1}
                        justifyContent={
                          device.composableDevice.controlUnits.length > 1
                            ? 'space-around'
                            : 'flex-start'
                        }
                      >
                        {device.composableDevice.controlUnits.map(
                          (controlUnit, j) => {
                            const zoneIndex = parseInt(
                              device.zoneMapping[j + 1]
                            );
                            const zone =
                              flexeserveDevice?.shelfSettings[zoneIndex];
                            if (!zone) return null;
                            return (
                              <ZoneManagerConnected
                                size="small"
                                key={`${controlUnit.controlUnitId}`}
                                item={{
                                  device: {
                                    ...flexeserveDevice,
                                    composableDevice: device.composableDevice,
                                  } as any,
                                  zone,
                                }}
                                zoneIndex={zoneIndex}
                                loadingState={isFetchingFlexeserveDevice}
                                sx={[
                                  device.composableDevice.controlUnits.length >
                                  1
                                    ? {
                                        marginTop: 0,
                                      }
                                    : {
                                        marginTop: '8px',
                                      },
                                ]}
                              />
                            );
                          }
                        )}
                      </Stack>
                    </td>
                    <td>
                      {
                        <Button
                          sx={{
                            padding: 3,
                            borderRadius: '1em',
                            height: '85px',
                            width: '85px',
                          }}
                          variant="contained"
                          onClick={() => {
                            reinstallNode({
                              commanderId,
                              nodeNumber: parseInt(
                                device.composableDevice.labels[
                                  'node.nodeNumber'
                                ]
                              ),
                              nodeType:
                                device.composableDevice.labels['node.nodeType'],
                            });
                          }}
                          loading={isReinstallingNode}
                        >
                          <ReinstallIcon height={'3em'} />
                        </Button>
                      }
                    </td>
                    <td>
                      {updating && (
                        <IconButton
                          sx={(theme) => ({
                            padding: 3,
                            borderRadius: '1em',
                            height: '85px',
                            width: '85px',
                            borderColor: theme.palette.error.main,
                            color: theme.palette.error.main,
                          })}
                          variant="outlined"
                          loading={isDeletingFlexeserveDevice}
                          disabled={isReinstallingNode}
                          onClick={() => {
                            setDeviceToDelete(device);
                            setDisplayConfirmModal(true);
                          }}
                        >
                          <TrashIcon height={'3em'} />
                        </IconButton>
                      )}
                    </td>
                  </Paper>
                );
              })}
            </tbody>
          </Table>
          {getIn(formik.errors, 'serialnumberError') && (
            <Alert severity="error" sx={{ my: 2 }}>
              {t(`${getIn(formik.errors, 'serialnumberError')}`)}
            </Alert>
          )}
          {getIn(formik.errors, 'macAddressError') && (
            <Alert severity="error" sx={{ my: 2 }}>
              {t(`${getIn(formik.errors, 'macAddressError')}`)}
            </Alert>
          )}
        </Container>
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Grid container spacing={2} justifyContent={'space-between'} mt={2}>
            <Grid
              size={{
                xs: 12,
                sm: 2,
              }}
            >
              <BackButton onClick={createOrUpdateDeviceBack} />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 2,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                rounded
                fullWidth
                loading={formik.isSubmitting}
                disabled={!formik.isValid}
                onClick={() => formik.handleSubmit()}
              >
                {t('button.next')}
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Container>
      <ModalInfo
        PaperProps={{
          sx: {
            padding: '2rem 4rem',
            maxWidth: '70vw',
            width: '100%',
          },
        }}
        open={displayConfirmModal}
        description={
          <Typography
            variant="body1"
            component={'p'}
            p="2rem 0 1rem 0"
            align="center"
          >
            {Object.keys(deviceToDelete?.zoneMapping || {}).length === 0
              ? t('initial_setup.connect_commander.removing_node_message')
              : deviceToDelete && !isNewNode(deviceToDelete, devices)
              ? t(
                  'initial_setup.connect_commander.remove_node_and_remove_device'
                )
              : t('initial_setup.connect_commander.removing_new_node_message')}
          </Typography>
        }
        title={
          <Typography fontSize="1.5rem" align="center">
            {t('initial_setup.connect_commander.removing_node_title', {
              device:
                deviceToDelete && !isNewNode(deviceToDelete, devices)
                  ? deviceToDelete?.name
                  : deviceToDelete?.mac,
            })}
          </Typography>
        }
        buttonActions={
          <Stack
            direction="row"
            spacing={2}
            justifyContent="space-evenly"
            width={'100%'}
          >
            <Button
              autoFocus
              variant="outlined"
              rounded
              disabled={isDeletingFlexeserveDevice || isRemovingNode}
              onClick={() => {
                setDisplayConfirmModal(false);
                setDeviceToDelete(null);
              }}
              sx={{ flex: '1 1 0px' }}
            >
              {t('button.cancel')}
            </Button>
            {deviceToDelete && !isNewNode(deviceToDelete, devices) ? (
              <Button
                autoFocus
                variant="contained-gray"
                rounded
                disabled={isDeletingFlexeserveDevice || isRemovingNode}
                onClick={() =>
                  onDelete({
                    shouldRemoveFlexeserveDevice: true,
                    shouldRemoveNode: false,
                  })
                }
                sx={{ flex: '1 1 0px' }}
              >
                {isDeletingFlexeserveDevice ? (
                  <CircularProgress size="1rem" />
                ) : (
                  t('initial_setup.connect_commander.remove_only_device')
                )}
              </Button>
            ) : null}
            {Object.keys(deviceToDelete?.zoneMapping || {}).length !== 0 ? (
              <Button
                autoFocus
                variant="contained"
                rounded
                disabled={isDeletingFlexeserveDevice || isRemovingNode}
                onClick={() =>
                  onDelete({
                    shouldRemoveNode: true,
                    shouldRemoveFlexeserveDevice: !isNewNode(
                      deviceToDelete,
                      devices
                    ),
                  })
                }
                sx={{ flex: '1 1 0px' }}
              >
                {isDeletingFlexeserveDevice || isRemovingNode ? (
                  <CircularProgress
                    size="1rem"
                    sx={(theme) => ({
                      color: theme.palette.common.white,
                    })}
                  />
                ) : (
                  t('initial_setup.connect_commander.accept_and_remove_node', {
                    device: isNewNode(deviceToDelete, devices)
                      ? ''
                      : t('initial_setup.connect_commander.and_the_device'),
                  })
                )}
              </Button>
            ) : null}
          </Stack>
        }
      />
    </>
  );
}

export default DevicesSetUp;

const Badge = styled(Box)<{ borderColor: string; bgcolor: string }>(
  ({ borderColor, bgcolor }) => ({
    margin: 'auto',
    borderLeft: `10px solid ${borderColor}`,
    background: bgcolor,
    display: 'flex',
    alignItems: 'center',
    width: 'fit-content',
    padding: '0.2em 1em',
    position: 'absolute',
    top: 0,
    left: 0,
    opacity: 0.8,
  })
);
